import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Button,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { getGiftCardSales, getGiftCardSalesByWaiter, getFullGiftCardSoldReport } from '../services/reportService';
import { format, startOfWeek } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PictureAsPdf } from '@mui/icons-material';

const GiftcardPurchaseReport = () => {
    // State for date range - default to last 90 days
    const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());

    // State for data
    const [purchaseData, setPurchaseData] = useState([]);
    const [waiterData, setWaiterData] = useState([]);
    const [fullSoldData, setFullSoldData] = useState([]);
    const [loading, setLoading] = useState(false);

    // State for table sorting and pagination
    const [sortBy, setSortBy] = useState('SoldDate');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [page, setPage] = useState(0); // MUI uses 0-based pages
    const [pageSize, setPageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // State for export
    const [exporting, setExporting] = useState(false);

    // Load data
    const loadReportsData = async () => {
        setLoading(true);
        try {
            // Set start date to beginning of day to include all transactions on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all transactions on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            // Load all reports in parallel
            const [salesResponse, waiterResponse, fullSoldResponse] = await Promise.all([
                getGiftCardSales(startOfDay, endOfDay),
                getGiftCardSalesByWaiter(startOfDay, endOfDay),
                getFullGiftCardSoldReport(startOfDay, endOfDay, sortBy, sortOrder, page + 1, pageSize)
            ]);
            
            // Transform sales data for line chart
            const salesData = salesResponse.purchaseData || [];
            const salesChartData = transformDataForChart(salesData);
            setPurchaseData(salesChartData);
            
            // Set waiter data for bar chart
            const waiterData = waiterResponse.waiterData || [];
            setWaiterData(waiterData);

            // Set full sold data for table
            const fullSoldData = fullSoldResponse.fullSoldData || [];
            setFullSoldData(fullSoldData);
            setTotalCount(fullSoldResponse.pagination?.totalCount || 0);
        } catch (error) {
            console.error('Failed to load reports data:', error);
            // TODO: Add error notification
        } finally {
            setLoading(false);
        }
    };

    // Transform data from backend format to chart format
    const transformDataForChart = (rawData) => {
        // First, collect all unique store names and week starts
        const allStores = new Set();
        const allWeeks = new Set();
        
        rawData.forEach(item => {
            allStores.add(item.StoreName);
            allWeeks.add(item.WeekStart);
        });

        // Generate all weeks in the date range to ensure we show weeks with 0 sales
        const startOfRange = new Date(startDate);
        const endOfRange = new Date(endDate);
        
        // Generate all Monday dates (week starts) in the range
        const allWeeksInRange = [];
        const current = new Date(startOfRange);
        
        // Find the first Monday in the range
        while (current.getDay() !== 1) { // 1 = Monday
            current.setDate(current.getDate() - 1);
        }
        
        // Generate all Mondays in the range
        while (current <= endOfRange) {
            // Use date string for consistent key generation - force to UTC midnight
            const weekKey = new Date(current.getFullYear(), current.getMonth(), current.getDate()).toISOString().split('T')[0];
            allWeeksInRange.push(weekKey);
            current.setDate(current.getDate() + 7); // Move to next Monday
        }

        // Group data by week start date
        const weeklyData = {};
        
        // First, create entries for all weeks in range with 0 values
        allWeeksInRange.forEach(weekKey => {
            // Parse the date string directly to avoid timezone conversion
            const [year, month, day] = weekKey.split('-').map(Number);
            const localDate = new Date(year, month - 1, day); // month is 0-indexed
            const formattedWeekStart = format(localDate, 'MMM dd');
            
            weeklyData[weekKey] = {
                weekStart: formattedWeekStart,
                weekStartFull: weekKey
            };
            
            // Initialize all stores with 0 for this week
            allStores.forEach(storeName => {
                weeklyData[weekKey][storeName] = 0;
            });
        });
        
        // Then, update with actual data from API
        rawData.forEach(item => {
            // Convert API week start to consistent date format
            // Use split to avoid timezone conversion - API returns local date as UTC midnight
            const weekKey = item.WeekStart.split('T')[0];
            
            if (weeklyData[weekKey]) {
                weeklyData[weekKey][item.StoreName] = item.GiftcardsSold;
            }
        });

        // Convert to array and sort by date
        const chartData = Object.values(weeklyData).sort((a, b) => 
            new Date(a.weekStartFull) - new Date(b.weekStartFull)
        );
        
        return chartData;
    };

    // Effect to load data when dependencies change
    useEffect(() => {
        loadReportsData();
    }, [startDate, endDate, sortBy, sortOrder, page, pageSize]);

    // Handle sort change
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'ASC';
        setSortOrder(isAsc ? 'DESC' : 'ASC');
        setSortBy(column);
        setPage(0); // Reset to first page when sorting
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Export to PDF with visual charts and table
    const handleExportPDF = async () => {
        setExporting(true);
        try {
            // Create a new PDF document in portrait orientation
            const pdf = new jsPDF('p', 'mm', 'a4'); // 'p' for portrait
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            let yPosition = 20;

            // Helper function to add text with word wrap
            const addText = (text, x, y, maxWidth, fontSize = 10) => {
                pdf.setFontSize(fontSize);
                const lines = pdf.splitTextToSize(text, maxWidth);
                pdf.text(lines, x, y);
                return y + (lines.length * fontSize * 0.4);
            };

            // Helper function to add a new page if needed
            const checkNewPage = (requiredSpace) => {
                if (yPosition + requiredSpace > pageHeight - 20) {
                    pdf.addPage();
                    yPosition = 20;
                    return true;
                }
                return false;
            };

            // Helper function to add image to PDF
            const addImageToPDF = async (elementId, title, maxHeight = 120) => {
                const element = document.getElementById(elementId);
                if (!element) return yPosition;

                try {
                    // Add a small delay to ensure charts are fully rendered
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    const canvas = await html2canvas(element, {
                        scale: 2, // Higher resolution
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#ffffff',
                        logging: false,
                        foreignObjectRendering: true, // Better support for SVG elements
                        removeContainer: false,
                        ignoreElements: (element) => {
                            // Don't ignore any elements to capture labels
                            return false;
                        }
                    });

                    const imgData = canvas.toDataURL('image/png');
                    const imgWidth = pageWidth - 40;
                    const imgHeight = Math.min((canvas.height * imgWidth) / canvas.width, maxHeight);

                    // Add title
                    checkNewPage(30);
                    yPosition = addText(title, 20, yPosition, pageWidth - 40, 14);
                    yPosition += 10;

                    // Add image
                    checkNewPage(imgHeight + 10);
                    pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
                    yPosition += imgHeight + 15;

                    return yPosition;
                } catch (error) {
                    console.error(`Error capturing ${elementId}:`, error);
                    yPosition = addText(`${title} - Error capturing image`, 20, yPosition, pageWidth - 40, 10);
                    yPosition += 15;
                    return yPosition;
                }
            };

            // Title
            yPosition = addText('Giftcard Sold Report', 20, yPosition, pageWidth - 40, 16);
            yPosition += 5;
            yPosition = addText(`Date Range: ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`, 20, yPosition, pageWidth - 40, 12);
            yPosition = addText(`Generated on: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, 20, yPosition, pageWidth - 40, 10);
            yPosition += 15;

            // Add Weekly Sales by Store Chart
            yPosition = await createCustomWeeklyChartForPDF(pdf, purchaseData, yPosition, pageWidth, pageHeight);

            // Add Weekly Sales Table
            yPosition = await createWeeklySalesTableForPDF(pdf, purchaseData, yPosition, pageWidth, pageHeight);

            // Add Sales by Waiter Chart
            yPosition = await createCustomWaiterChartForPDF(pdf, waiterData, yPosition, pageWidth, pageHeight);

            // For the table, we need to capture it differently since it's paginated
            // Let's get all data and create a visual table
            // Set start date to beginning of day to include all transactions on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all transactions on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            // Get all records for PDF export by setting a very large page size and exportAll=true
            const fullSoldResponse = await getFullGiftCardSoldReport(startOfDay, endOfDay, sortBy, sortOrder, 1, 100000, true);
            const fullData = fullSoldResponse.fullSoldData || [];
            

            if (fullData.length > 0) {
                // Add title
                checkNewPage(30);
                yPosition = addText('Full Giftcard Sold Report', 20, yPosition, pageWidth - 40, 14);
                yPosition += 10;
                
                // Create table using PDF's built-in functionality
                const tableData = fullData.map((item, index) => [
                    item.GiftcardNo || '-',
                    item.SoldStoreName || '-',
                    item.SoldWaiterLoginName || '-',
                    item.SoldDate ? item.SoldDate.replace('T', ' ').replace(/\.\d{3}Z?$/, '') : '-'
                ]);
                
                
                // Add table headers
                const headers = ['Giftcard No.', 'Store Name', 'Waiter Login Name', 'Sold Date'];
                
                // Calculate column widths for portrait A4 (210mm - 40mm margins = 170mm)
                const colWidths = [35, 45, 45, 45]; // Total: 170mm, fits in portrait A4 width
                
                // Create table rows
                const tableRows = [headers, ...tableData];
                
                // Add table to PDF
                try {
                    // Simple table implementation
                    const rowHeight = 8;
                    const startY = yPosition;
                    
                    // Draw table headers
                    pdf.setFontSize(10);
                    pdf.setFont(undefined, 'bold');
                    let currentX = 20;
                    headers.forEach((header, colIndex) => {
                        pdf.text(header, currentX, yPosition);
                        currentX += colWidths[colIndex];
                    });
                    yPosition += rowHeight + 2; // Add extra spacing after headers
                    
                    // Draw horizontal line under headers
                    pdf.line(20, yPosition, 20 + colWidths.reduce((a, b) => a + b, 0), yPosition);
                    yPosition += 4; // Add more spacing after the line
                    
                    // Draw table data
                    pdf.setFont(undefined, 'normal');
                    tableData.forEach((row, rowIndex) => {
                        // Check if we need a new page (portrait mode)
                        if (yPosition > pageHeight - 30) {
                            pdf.addPage();
                            yPosition = 20;
                        }
                        
                        currentX = 20;
                        row.forEach((cell, colIndex) => {
                            let displayText = cell;
                            
                            // Special handling for different columns
                            if (colIndex === 3) { // Sold Date column
                                // For dates, try to show more of the date part
                                if (cell.includes('T')) {
                                    // Format: YYYY-MM-DD HH:MM:SS -> YYYY-MM-DD HH:MM
                                    displayText = cell.replace('T', ' ').replace(/\.\d{3}Z?$/, '').substring(0, 16);
                                } else {
                                    displayText = cell;
                                }
                            } else {
                                // For other columns, use standard truncation
                                const maxLength = Math.floor(colWidths[colIndex] / 2);
                                displayText = cell.length > maxLength ? cell.substring(0, maxLength - 3) + '...' : cell;
                            }
                            
                            pdf.text(displayText, currentX, yPosition);
                            currentX += colWidths[colIndex];
                        });
                        yPosition += rowHeight;
                    });
                    
                    // Add total count
                    yPosition += 10;
                    pdf.setFontSize(10);
                    pdf.text(`Total Records: ${fullData.length}`, 20, yPosition);
                    yPosition += 15;
                    
                    
                } catch (error) {
                    console.error('Error creating PDF table:', error);
                    yPosition = addText('Full Giftcard Sold Report - Error creating table', 20, yPosition, pageWidth - 40, 10);
                    yPosition += 15;
                }
            }

            // Save the PDF
            const fileName = `Giftcard_Sold_Report_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}.pdf`;
            pdf.save(fileName);

        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    // Create weekly sales table for PDF
    const createWeeklySalesTableForPDF = async (pdf, purchaseData, yPosition, pageWidth, pageHeight) => {
        if (!purchaseData || purchaseData.length === 0) {
            pdf.setFontSize(10);
            pdf.text('No weekly sales data available for table', 20, yPosition);
            return yPosition + 15;
        }

        // Get store names from the data
        const storeNames = getStoreNames();
        if (storeNames.length === 0) {
            pdf.setFontSize(10);
            pdf.text('No store data available for table', 20, yPosition);
            return yPosition + 15;
        }

        // Create a temporary container for the table
        const tempTableContainer = document.createElement('div');
        tempTableContainer.style.position = 'absolute';
        tempTableContainer.style.left = '-9999px';
        tempTableContainer.style.top = '0';
        tempTableContainer.style.backgroundColor = '#ffffff';
        tempTableContainer.style.padding = '20px';
        tempTableContainer.style.width = '800px';
        tempTableContainer.style.fontFamily = 'Arial, sans-serif';

        // Create table HTML
        let tableHTML = `
            <div style="width: 100%;">
                <h3 style="margin: 0 0 15px 0; font-size: 16px; text-align: center;">Weekly Giftcard Sales by Store</h3>
                <table style="border-collapse: collapse; width: 100%; font-size: 11px;">
                    <thead>
                        <tr style="background-color: #f5f5f5;">
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Week</th>
        `;

        // Add store columns
        storeNames.forEach(storeName => {
            tableHTML += `<th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">${storeName}</th>`;
        });

        tableHTML += `
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">Total</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        // Add data rows
        purchaseData.forEach((week, index) => {
            const rowColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
            let totalForWeek = 0;
            
            tableHTML += `
                <tr style="background-color: ${rowColor};">
                    <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold;">${week.weekStart}</td>
            `;

            // Add data for each store
            storeNames.forEach(storeName => {
                const value = week[storeName] || 0;
                totalForWeek += value;
                tableHTML += `<td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${value}</td>`;
            });

            // Add total column
            tableHTML += `<td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold; background-color: #e8f4fd;">${totalForWeek}</td>`;

            tableHTML += `</tr>`;
        });

        // Add totals row
        tableHTML += `
                        <tr style="background-color: #e8f4fd; font-weight: bold;">
                            <td style="border: 1px solid #ddd; padding: 8px;">TOTAL</td>
        `;

        let grandTotal = 0;
        storeNames.forEach(storeName => {
            const storeTotal = purchaseData.reduce((sum, week) => sum + (week[storeName] || 0), 0);
            grandTotal += storeTotal;
            tableHTML += `<td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${storeTotal}</td>`;
        });

        tableHTML += `
                            <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${grandTotal}</td>
                        </tr>
                    </tbody>
                </table>
                <p style="margin: 10px 0 0 0; font-size: 10px; color: #666; text-align: center;">
                    Table shows giftcard sales per week for each store
                </p>
            </div>
        `;

        tempTableContainer.innerHTML = tableHTML;
        document.body.appendChild(tempTableContainer);

        try {
            const canvas = await html2canvas(tempTableContainer, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: 800,
                height: Math.min(tempTableContainer.scrollHeight, 600)
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - 40;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Check if we need a new page
            if (yPosition + imgHeight + 30 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }

            // Add title
            pdf.setFontSize(14);
            pdf.text('Weekly Giftcard Sales Summary Table', 20, yPosition);
            yPosition += 10;

            // Add image
            if (yPosition + imgHeight + 10 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }
            pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;

        } catch (error) {
            console.error('Error creating weekly sales table:', error);
            pdf.setFontSize(10);
            pdf.text('Weekly Sales Table - Table creation failed', 20, yPosition);
            yPosition += 15;
        } finally {
            document.body.removeChild(tempTableContainer);
        }

        return yPosition;
    };

    // Create custom waiter chart for PDF with visible labels
    const createCustomWaiterChartForPDF = async (pdf, waiterData, yPosition, pageWidth, pageHeight) => {
        if (!waiterData || waiterData.length === 0) return yPosition;

        // Create a temporary container for the custom chart
        const tempChartContainer = document.createElement('div');
        tempChartContainer.style.position = 'absolute';
        tempChartContainer.style.left = '-9999px';
        tempChartContainer.style.top = '0';
        tempChartContainer.style.backgroundColor = '#ffffff';
        tempChartContainer.style.padding = '20px';
        tempChartContainer.style.width = '800px';
        tempChartContainer.style.fontFamily = 'Arial, sans-serif';

        // Find max value for scaling
        const maxValue = Math.max(...waiterData.map(item => item.GiftcardsSold));
        const chartHeight = 300;
        const chartWidth = 700;
        const barWidth = chartWidth / waiterData.length - 10;
        const maxBarHeight = chartHeight - 60; // Leave space for labels

        let chartHTML = `
            <div style="width: 100%; text-align: center;">
                <h3 style="margin: 0 0 20px 0; font-size: 18px;">Giftcard Sales by Waiter</h3>
                <svg width="${chartWidth}" height="${chartHeight}" style="border: 1px solid #ddd;">
        `;

        // Add bars and labels
        waiterData.forEach((item, index) => {
            const barHeight = (item.GiftcardsSold / maxValue) * maxBarHeight;
            const x = index * (chartWidth / waiterData.length) + 20;
            const y = chartHeight - barHeight - 40;
            
            // Bar
            chartHTML += `
                <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" 
                      fill="#8884d8" stroke="#666" stroke-width="1" rx="4" ry="4"/>
            `;
            
            // Number label inside bar (centered)
            const labelX = x + barWidth / 2;
            const labelY = y + barHeight / 2;
            chartHTML += `
                <text x="${labelX}" y="${labelY}" text-anchor="middle" dominant-baseline="middle" 
                      fill="#ffffff" font-size="16" font-weight="bold">${item.GiftcardsSold}</text>
            `;
            
            // Waiter name below bar
            const nameY = chartHeight - 20;
            chartHTML += `
                <text x="${labelX}" y="${nameY}" text-anchor="middle" dominant-baseline="middle" 
                      fill="#333" font-size="12">${item.WaiterName}</text>
            `;
        });

        chartHTML += `
                </svg>
                <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
                    Total Waiters: ${waiterData.length} | Total Giftcards: ${waiterData.reduce((sum, item) => sum + item.GiftcardsSold, 0)}
                </p>
            </div>
        `;

        tempChartContainer.innerHTML = chartHTML;
        document.body.appendChild(tempChartContainer);

        try {
            const canvas = await html2canvas(tempChartContainer, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: 800,
                height: tempChartContainer.scrollHeight
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - 40;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add title
            if (yPosition + imgHeight + 30 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }
            pdf.setFontSize(14);
            pdf.text('Giftcard Sales by Waiter', 20, yPosition);
            yPosition += 10;

            // Add image
            if (yPosition + imgHeight + 10 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }
            pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;

        } catch (error) {
            console.error('Error creating custom chart:', error);
            pdf.setFontSize(10);
            pdf.text('Giftcard Sales by Waiter - Chart creation failed', 20, yPosition);
            yPosition += 15;
        } finally {
            document.body.removeChild(tempChartContainer);
        }

        return yPosition;
    };

    // Create custom weekly sales chart for PDF
    const createCustomWeeklyChartForPDF = async (pdf, purchaseData, yPosition, pageWidth, pageHeight) => {
        if (!purchaseData || purchaseData.length === 0) {
            pdf.setFontSize(10);
            pdf.text('No weekly sales data available', 20, yPosition);
            return yPosition + 15;
        }

        // Create a temporary container for the custom chart
        const tempChartContainer = document.createElement('div');
        tempChartContainer.style.position = 'absolute';
        tempChartContainer.style.left = '-9999px';
        tempChartContainer.style.top = '0';
        tempChartContainer.style.backgroundColor = '#ffffff';
        tempChartContainer.style.padding = '20px';
        tempChartContainer.style.width = '800px';
        tempChartContainer.style.fontFamily = 'Arial, sans-serif';

        // Get unique store names and colors
        const storeNames = getStoreNames();
        const colors = [
            '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', 
            '#ff00ff', '#00ffff', '#ff0000', '#0000ff', '#ffff00'
        ];

        const chartHeight = 400; // Standard chart height
        const chartWidth = 700;
        const marginTop = 40;
        const marginBottom = 60; // Standard bottom margin
        const marginLeft = 80;
        const marginRight = 40;
        const plotHeight = chartHeight - marginTop - marginBottom;
        const plotWidth = chartWidth - marginLeft - marginRight;
        const finalChartHeight = chartHeight; // No need to adjust for internal legend

        // Find min/max values for scaling
        let minValue = Infinity;
        let maxValue = -Infinity;
        purchaseData.forEach(week => {
            storeNames.forEach(storeName => {
                if (week[storeName] !== undefined) {
                    minValue = Math.min(minValue, week[storeName]);
                    maxValue = Math.max(maxValue, week[storeName]);
                }
            });
        });

        if (minValue === Infinity) minValue = 0;
        if (maxValue === -Infinity) maxValue = 10;
        
        // Ensure we have a proper range for scaling
        if (minValue === maxValue) {
            // If all values are the same, create a range around that value
            if (maxValue === 0) {
                minValue = 0;
                maxValue = 5;
            } else {
                minValue = Math.max(0, maxValue - 2);
                maxValue = maxValue + 2;
            }
        }

        // Add Y-axis labels - create clean integer steps
        const valueRange = maxValue - minValue;
        
        // Always use clean integer steps for better readability
        let stepSize = 1;
        let niceMinValue = Math.max(0, Math.floor(minValue));
        let niceMaxValue = Math.ceil(maxValue);
        
        // If the range is small (0-5), use steps of 1
        // If the range is larger, use appropriate integer steps
        if (niceMaxValue - niceMinValue > 10) {
            stepSize = Math.ceil((niceMaxValue - niceMinValue) / 10);
        }
        
        // Generate clean integer labels
        const labels = [];
        for (let value = niceMinValue; value <= niceMaxValue; value += stepSize) {
            labels.push(value);
        }
        
        let chartHTML = `
            <div style="width: 100%; text-align: center;">
                <h3 style="margin: 0 0 20px 0; font-size: 18px;">Weekly Giftcard Sales by Store</h3>
                <svg width="${chartWidth}" height="${finalChartHeight}" style="border: 1px solid #ddd;">
                    <!-- Background -->
                    <rect width="${plotWidth}" height="${plotHeight}" x="${marginLeft}" y="${marginTop}" fill="#ffffff" />
        `;

        // Add horizontal grid lines that align with Y-axis labels
        labels.forEach((label, index) => {
            const y = marginTop + plotHeight - ((label - niceMinValue) / (niceMaxValue - niceMinValue)) * plotHeight;
            chartHTML += `
                <line x1="${marginLeft}" y1="${y}" x2="${marginLeft + plotWidth}" y2="${y}" stroke="#e0e0e0" stroke-width="1"/>
            `;
        });
        
        // Add Y-axis labels
        labels.forEach((label, index) => {
            const y = marginTop + plotHeight - ((label - niceMinValue) / (niceMaxValue - niceMinValue)) * plotHeight;
            chartHTML += `
                <text x="${marginLeft - 10}" y="${y + 4}" text-anchor="end" fill="#666" font-size="10">${label}</text>
            `;
        });

        // Add X-axis labels (week starts)
        purchaseData.forEach((week, index) => {
            let x;
            if (purchaseData.length === 1) {
                // Single data point - center it in the plot area
                x = marginLeft + plotWidth / 2;
            } else {
                // Multiple data points - distribute evenly
                x = marginLeft + (index / (purchaseData.length - 1)) * plotWidth;
            }
            
            chartHTML += `
                <text x="${x}" y="${marginTop + plotHeight + 20}" text-anchor="middle" fill="#666" font-size="10" transform="rotate(-45, ${x}, ${marginTop + plotHeight + 20})">
                    ${week.weekStart}
                </text>
            `;
        });

        // Add lines for each store
        storeNames.forEach((storeName, storeIndex) => {
            const color = colors[storeIndex % colors.length];
            let pathData = '';
            
            purchaseData.forEach((week, index) => {
                if (week[storeName] !== undefined) {
                    let x;
                    if (purchaseData.length === 1) {
                        // Single data point - center it in the plot area
                        x = marginLeft + plotWidth / 2;
                    } else {
                        // Multiple data points - distribute evenly
                        x = marginLeft + (index / (purchaseData.length - 1)) * plotWidth;
                    }
                    
                    const y = marginTop + plotHeight - ((week[storeName] - niceMinValue) / (niceMaxValue - niceMinValue)) * plotHeight;
                    
                    if (index === 0) {
                        pathData += `M ${x} ${y}`;
                    } else {
                        pathData += ` L ${x} ${y}`;
                    }
                    
                    // Add dots
                    chartHTML += `
                        <circle cx="${x}" cy="${y}" r="4" fill="${color}" stroke="#fff" stroke-width="2"/>
                    `;
                }
            });
            
            if (pathData) {
                chartHTML += `
                    <path d="${pathData}" fill="none" stroke="${color}" stroke-width="2"/>
                `;
            }
        });

        // Legend is now handled below the SVG chart

        // Add legend below the chart
        let textLegendHTML = '';
        if (storeNames.length > 0) {
            textLegendHTML = '<div style="margin-top: 15px; padding: 10px; background-color: #f9f9f9; border-radius: 5px;">';
            textLegendHTML += '<h4 style="margin: 0 0 10px 0; font-size: 14px; color: #333;">Store Legend:</h4>';
            textLegendHTML += '<div style="display: flex; flex-wrap: wrap; gap: 15px;">';
            
            storeNames.forEach((storeName, index) => {
                const color = colors[index % colors.length];
                textLegendHTML += `
                    <div style="display: flex; align-items: center; gap: 5px;">
                        <div style="width: 16px; height: 16px; background-color: ${color}; border: 2px solid #333; border-radius: 2px;"></div>
                        <span style="font-size: 12px; font-weight: bold;">${storeName}</span>
                    </div>
                `;
            });
            
            textLegendHTML += '</div></div>';
        }

        chartHTML += `
                </svg>
                ${textLegendHTML}
                <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
                    Data points represent Monday-Sunday weeks. Labels show the Monday of each week.
                </p>
            </div>
        `;

        tempChartContainer.innerHTML = chartHTML;
        document.body.appendChild(tempChartContainer);

        try {
            const canvas = await html2canvas(tempChartContainer, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: 800,
                height: tempChartContainer.scrollHeight
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - 40;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add title
            if (yPosition + imgHeight + 30 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }
            pdf.setFontSize(14);
            pdf.text('Weekly Giftcard Sales by Store', 20, yPosition);
            yPosition += 10;

            // Add image
            if (yPosition + imgHeight + 10 > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
            }
            pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;

        } catch (error) {
            console.error('Error creating weekly chart:', error);
            pdf.setFontSize(10);
            pdf.text('Weekly Sales Chart - Chart creation failed', 20, yPosition);
            yPosition += 15;
        } finally {
            document.body.removeChild(tempChartContainer);
        }

        return yPosition;
    };

    // Get unique store names for legend
    const getStoreNames = () => {
        const storeNames = new Set();
        purchaseData.forEach(week => {
            Object.keys(week).forEach(key => {
                if (key !== 'weekStart' && key !== 'weekStartFull') {
                    storeNames.add(key);
                }
            });
        });
        return Array.from(storeNames);
    };

    // Generate colors for different stores
    const colors = [
        '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
        '#ff00ff', '#00ffff', '#ffff00', '#ff0000', '#0000ff'
    ];

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Giftcard Sold Report
            </Typography>

            {/* Date Range Controls */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <DesktopDatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            slotProps={{ textField: { variant: 'outlined' } }}
                        />
                        <DesktopDatePicker
                            label="End Date"
                            value={endDate}
                            onChange={setEndDate}
                            slotProps={{ textField: { variant: 'outlined' } }}
                        />
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdf />}
                        onClick={handleExportPDF}
                        disabled={exporting || loading}
                        sx={{ minWidth: 120 }}
                    >
                        {exporting ? 'Exporting...' : 'Export PDF'}
                    </Button>
                </Stack>
            </Paper>

            {/* Weekly Sales by Store Chart */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Weekly Giftcard Sales by Store
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Data points represent Monday-Sunday weeks. Labels show the Monday of each week.
                </Typography>
                
                {loading ? (
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>Loading...</Typography>
                    </Box>
                ) : purchaseData.length === 0 ? (
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>No data available for the selected date range.</Typography>
                    </Box>
                ) : (
                    <Box id="weekly-sales-chart">
                        <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                            data={purchaseData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="weekStart" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                            />
                            <YAxis 
                                label={{ value: 'Giftcards Sold', angle: -90, position: 'insideLeft' }}
                                allowDecimals={false}
                            />
                            <Tooltip 
                                labelFormatter={(label, payload) => {
                                    if (payload && payload[0]) {
                                        const data = payload[0].payload;
                                        return `Week of ${format(new Date(data.weekStartFull), 'MMM dd, yyyy')}`;
                                    }
                                    return label;
                                }}
                                formatter={(value, name) => [value, name]}
                            />
                            <Legend />
                            {getStoreNames().map((storeName, index) => (
                                <Line
                                    key={storeName}
                                    type="linear"
                                    dataKey={storeName}
                                    stroke={colors[index % colors.length]}
                                    strokeWidth={2}
                                    dot={{ r: 4, fill: colors[index % colors.length] }}
                                    activeDot={{ r: 6 }}
                                    connectNulls={false}
                                />
                            ))}
                        </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Paper>

            {/* Sold By Waiter Chart */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Giftcard Sales by Waiter
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total giftcards sold by each waiter for the selected period.
                </Typography>
                
                {loading ? (
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>Loading...</Typography>
                    </Box>
                ) : waiterData.length === 0 ? (
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>No waiter data available for the selected date range.</Typography>
                    </Box>
                ) : (
                    <Box id="waiter-sales-chart">
                        <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={waiterData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="WaiterName" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                            />
                            <YAxis 
                                label={{ value: 'Giftcards Sold', angle: -90, position: 'insideLeft' }}
                                allowDecimals={false}
                            />
                            <Tooltip 
                                formatter={(value, name) => [value, 'Giftcards Sold']}
                                labelFormatter={(label) => `Waiter: ${label}`}
                            />
                            <Bar 
                                dataKey="GiftcardsSold" 
                                fill="#8884d8"
                                radius={[4, 4, 0, 0]}
                                label={{ 
                                    position: 'inside', 
                                    formatter: (value) => value,
                                    style: { fontSize: '16px', fill: '#ffffff', fontWeight: 'bold' }
                                }}
                            />
                        </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Paper>

            {/* Full Giftcard Sold Report Table */}
            <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Full Giftcard Sold Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Detailed list of all giftcards sold within the selected date range.
                </Typography>
                
                {loading ? (
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>Loading...</Typography>
                    </Box>
                ) : fullSoldData.length === 0 ? (
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>No giftcard sales found for the selected date range.</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'GiftcardNo'}
                                                direction={sortBy === 'GiftcardNo' ? sortOrder.toLowerCase() : 'asc'}
                                                onClick={() => handleSort('GiftcardNo')}
                                            >
                                                Giftcard No.
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'SoldStoreName'}
                                                direction={sortBy === 'SoldStoreName' ? sortOrder.toLowerCase() : 'asc'}
                                                onClick={() => handleSort('SoldStoreName')}
                                            >
                                                Sold Store Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'SoldWaiterLoginName'}
                                                direction={sortBy === 'SoldWaiterLoginName' ? sortOrder.toLowerCase() : 'asc'}
                                                onClick={() => handleSort('SoldWaiterLoginName')}
                                            >
                                                Sold Waiter Login Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'SoldDate'}
                                                direction={sortBy === 'SoldDate' ? sortOrder.toLowerCase() : 'asc'}
                                                onClick={() => handleSort('SoldDate')}
                                            >
                                                Sold Date
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fullSoldData.map((row, index) => (
                                        <TableRow key={`${row.GiftcardNo}-${index}`}>
                                            <TableCell>{row.GiftcardNo}</TableCell>
                                            <TableCell>{row.SoldStoreName}</TableCell>
                                            <TableCell>{row.SoldWaiterLoginName}</TableCell>
                                            <TableCell>{row.SoldDate ? row.SoldDate.replace('T', ' ').replace(/\.\d{3}Z?$/, '') : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 100]}
                            labelDisplayedRows={({ from, to, count }) => 
                                `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`}
                            showFirstButton
                            showLastButton
                            disabled={totalCount === 0}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default GiftcardPurchaseReport;
